import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME } from 'alakai-aloha-shared';
import { useItineraryOffer } from 'alakai-aloha-shared/public-ui';
import { trackEvent } from '../../utils/analytics';
import { getRuntimeSiteContext } from '../../lib/siteContext';

const InterviewLanding = () => {
  const navigate = useNavigate();
  const { active: pilotActive, offer } = useItineraryOffer();
  const { islandConfig } = React.useMemo(() => getRuntimeSiteContext(), []);

  const processSteps = [
    {
      step: '1',
      title: 'Tell Us About Your Group',
      description: 'Share details about your traveling party, relationships, and group dynamics.',
      time: '2 min'
    },
    {
      step: '2',
      title: 'Share Your Travel Style',
      description: 'Help us understand your preferences for adventure, dining, activities, and pace.',
      time: '4 min'
    },
    {
      step: '3',
      title: 'Dates & Logistics',
      description: 'Provide your travel dates, lodging preferences, and any special requirements.',
      time: '3 min'
    },
    {
      step: '4',
      title: 'Get Your Custom Itinerary',
      description: `Register for free and receive your personalized ${islandConfig.shortTitle} adventure plan.`,
      time: '1 min'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{`Start Your ${islandConfig.shortTitle} Interview | ${SITE_NAME}`}</title>
      </Helmet>
      <div className="relative z-00">
      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-4 py-4 lg:py-12">
        <div className="text-center mb-8">
          <h1 className="text-4xl lg:text-6xl font-bold mb-6">
            Your Perfect {islandConfig.shortTitle} Adventure Awaits
          </h1>
          
          <p className="text-xl lg:text-2xl mb-8 max-w-4xl mx-auto leading-relaxed">
            {pilotActive
              ? 'Your complete itinerary is free during our 30-day pilot. No credit card required.'
              : `Complete our personalized interview and receive a custom itinerary designed specifically for your ${islandConfig.shortTitle} interests, pace, and travel style.`}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-0">
            <button
              onClick={() => {
                trackEvent('interview_started', { event_category: 'conversion', event_label: 'landing_page_cta', ...(pilotActive && offer?.pilotId ? { pilot_id: offer.pilotId } : {}) });
                navigate('/interview/wizard');
              }}
              className="btn-primary-cta text-xl"
            >
              {pilotActive ? 'Plan My Trip Free' : 'Start the Interview'}
            </button>  
          </div>
        </div>


        {/* What to Expect */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-center mb-8">
            What to Expect in Your Interview
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="glass-card rounded-lg p-6 h-full">
                  <div className="flex items-center justify-between mb-4">
                    <div className="bg-white text-gray-900 rounded-full w-8 h-8 flex items-center justify-center font-bold">
                      {step.step}
                    </div>
                    <span className="text-sm font-medium opacity-80">
                      {step.time}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">
                    {step.title}
                  </h3>
                  <p className="text-sm leading-relaxed opacity-80">
                    {step.description}
                  </p>
                </div>
                
              </div>
            ))}
          </div>
        </div>


        {/* Final CTA */}
        <div className="text-center">
          <button
            onClick={() => {
              trackEvent('interview_started', { event_category: 'conversion', event_label: 'landing_page_final_cta', ...(pilotActive && offer?.pilotId ? { pilot_id: offer.pilotId } : {}) });
              navigate('/interview/wizard');
            }}
            className="btn-primary-cta text-xl py-4 px-12 mx-auto"
          >
            {pilotActive ? 'Plan My Trip Free' : 'Start the Interview'}
          </button>
          
        </div>
      </div>
      </div>
    </>
  );
};

export default InterviewLanding;
